import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { MODEL_PROVIDERS } from 'chat-shared'
import type { AdminModelEntry, ModelProvider } from 'chat-shared'
import { api, ApiError } from '../../api/client.js'
import {
  AdminCard,
  AdminPageHeader,
  AdminTableEmpty,
  adminTableSx,
} from '../../components/AdminUi.js'

const providerLabel: Record<ModelProvider, string> = {
  ionos: 'IONOS',
  bedrock: 'Bedrock (Anthropic)',
  'bedrock-converse': 'Bedrock (Converse)',
}

// Temperature-Obergrenze je Provider — muss zur Server-Validierung passen
// (apps/chat-api/src/http/admin.ts, PUT /models).
const temperatureMax = (provider: ModelProvider): number => (provider === 'ionos' ? 2 : 1)

const isModelProvider = (value: string): value is ModelProvider =>
  (MODEL_PROVIDERS as ReadonlyArray<string>).includes(value)

/**
 * Parst ein optionales numerisches Feld: leerer String ⇒ undefined (Override
 * entfernen), sonst Zahl; bei ungültiger Eingabe undefined + Fehlerflag.
 */
const parseOptionalNumber = (value: string): { value?: number; invalid: boolean } => {
  if (value.trim() === '') return { invalid: false }
  const parsed = Number(value)
  return Number.isFinite(parsed) ? { value: parsed, invalid: false } : { invalid: true }
}

// Formularzustand für Anlegen und Bearbeiten — beim Bearbeiten ist die
// Modell-ID fixiert (sie ist der Schlüssel des Katalogeintrags).
type ModelForm = {
  modelId: string
  label: string
  provider: ModelProvider
  reasoning: boolean
  inferenceProfile: boolean
  enabled: boolean
  maxTokens: string
  temperature: string
}

const emptyForm: ModelForm = {
  modelId: '',
  label: '',
  provider: 'ionos',
  reasoning: false,
  inferenceProfile: false,
  enabled: true,
  maxTokens: '',
  temperature: '',
}

const formFromEntry = (entry: AdminModelEntry): ModelForm => ({
  modelId: entry.id,
  label: entry.label,
  provider: entry.provider,
  reasoning: entry.reasoning,
  inferenceProfile: entry.inferenceProfile ?? false,
  enabled: entry.enabled,
  maxTokens: entry.maxTokens !== undefined ? String(entry.maxTokens) : '',
  temperature: entry.temperature !== undefined ? String(entry.temperature) : '',
})

/**
 * Einstellungen → Modelle (nur Systemadmins): DB-verwalteter Modellkatalog —
 * Modelle anlegen (Provider, Modell-ID, Label, Optionen), bearbeiten,
 * aktivieren/deaktivieren und löschen. Deaktivierte Modelle verschwinden aus
 * der Modellauswahl; unbekannte IDs lehnt der Server beim Senden ab.
 */
export const AdminModelsSection = () => {
  const [models, setModels] = useState<Array<AdminModelEntry>>([])
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | undefined>(undefined)
  const [form, setForm] = useState<ModelForm>(emptyForm)
  const [formError, setFormError] = useState<string | undefined>(undefined)
  const [deleteTarget, setDeleteTarget] = useState<AdminModelEntry | undefined>(undefined)
  const [notice, setNotice] = useState<string | undefined>(undefined)

  const reload = () => {
    api.listAdminModels().then(setModels).catch(() => setModels([]))
  }
  useEffect(reload, [])

  const openCreate = () => {
    setForm(emptyForm)
    setFormError(undefined)
    setDialogMode('create')
  }

  const openEdit = (model: AdminModelEntry) => {
    setForm(formFromEntry(model))
    setFormError(undefined)
    setDialogMode('edit')
  }

  const updateForm = <K extends keyof ModelForm>(key: K, value: ModelForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const handleSave = async () => {
    setFormError(undefined)

    if (form.modelId.trim().length === 0 || form.label.trim().length === 0) {
      setFormError('Modell-ID und Anzeigename sind erforderlich.')
      return
    }
    const maxTokens = parseOptionalNumber(form.maxTokens)
    const temperature = parseOptionalNumber(form.temperature)
    if (
      maxTokens.invalid ||
      (maxTokens.value !== undefined && (!Number.isInteger(maxTokens.value) || maxTokens.value < 1))
    ) {
      setFormError('Max. Tokens muss eine positive ganze Zahl sein.')
      return
    }
    const tempMax = temperatureMax(form.provider)
    if (
      temperature.invalid ||
      (temperature.value !== undefined && (temperature.value < 0 || temperature.value > tempMax))
    ) {
      setFormError(`Temperature muss zwischen 0 und ${tempMax} liegen.`)
      return
    }
    if (dialogMode === 'create' && models.some((model) => model.id === form.modelId.trim())) {
      setFormError('Ein Modell mit dieser ID existiert bereits.')
      return
    }

    try {
      await api.upsertAdminModel(form.modelId.trim(), {
        label: form.label.trim(),
        provider: form.provider,
        reasoning: form.reasoning,
        inferenceProfile:
          form.provider === 'bedrock-converse' && form.inferenceProfile ? true : undefined,
        enabled: form.enabled,
        maxTokens: maxTokens.value,
        temperature: temperature.value,
      })
      setDialogMode(undefined)
      reload()
    } catch (err) {
      setFormError(
        err instanceof ApiError && err.message === 'invalid_body'
          ? 'Bitte alle Felder korrekt ausfüllen (Modell-ID max. 50 Zeichen).'
          : 'Speichern fehlgeschlagen',
      )
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await api.deleteAdminModel(deleteTarget.id)
      setNotice(`Modell „${deleteTarget.label}“ gelöscht.`)
      setDeleteTarget(undefined)
      reload()
    } catch {
      setNotice(undefined)
      setDeleteTarget(undefined)
    }
  }

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {notice && (
        <Alert severity="success" onClose={() => setNotice(undefined)}>
          {notice}
        </Alert>
      )}
      <AdminPageHeader
        title="Modelle"
        description="DB-verwalteter Modellkatalog: Provider, Modell-ID und Parameter. Deaktivierte Modelle verschwinden aus der Modellauswahl."
        actions={
          <Button variant="contained" onClick={openCreate}>
            Neues Modell
          </Button>
        }
      />

      <AdminCard title={`Modellkatalog (${models.length})`}>
        <Table size="small" sx={adminTableSx}>
          <TableHead>
            <TableRow>
              <TableCell>Modell</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Max. Tokens</TableCell>
              <TableCell>Temperature</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {models.map((model) => (
              <TableRow key={model.id} hover onClick={() => openEdit(model)} sx={{ cursor: 'pointer' }}>
                <TableCell>
                  {model.label}
                  <Typography variant="caption" color="text.secondary" display="block">
                    {model.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    <Chip size="small" label={providerLabel[model.provider]} />
                    {model.reasoning && <Chip size="small" variant="outlined" label="Reasoning" />}
                    {model.inferenceProfile === true && (
                      <Chip size="small" variant="outlined" label="Inference-Profil" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>{model.maxTokens ?? 'Standard'}</TableCell>
                <TableCell>{model.temperature ?? 'Standard'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={model.enabled ? 'success' : 'default'}
                    label={model.enabled ? 'Aktiv' : 'Deaktiviert'}
                  />
                </TableCell>
              </TableRow>
            ))}
            {models.length === 0 && (
              <AdminTableEmpty colSpan={5}>Noch keine Modelle im Katalog.</AdminTableEmpty>
            )}
          </TableBody>
        </Table>
      </AdminCard>

      <Dialog
        open={dialogMode !== undefined}
        onClose={() => setDialogMode(undefined)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{dialogMode === 'create' ? 'Neues Modell' : form.label}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Modell-ID"
              size="small"
              value={form.modelId}
              onChange={(e) => updateForm('modelId', e.target.value)}
              disabled={dialogMode === 'edit'}
              required
              helperText="Exakte Provider-Modell-ID, z. B. anthropic.claude-sonnet-5 (max. 50 Zeichen)."
            />
            <TextField
              label="Anzeigename"
              size="small"
              value={form.label}
              onChange={(e) => updateForm('label', e.target.value)}
              required
            />
            <TextField
              label="Provider"
              select
              size="small"
              value={form.provider}
              onChange={(e) => {
                if (isModelProvider(e.target.value)) updateForm('provider', e.target.value)
              }}
            >
              {MODEL_PROVIDERS.map((provider) => (
                <MenuItem key={provider} value={provider}>
                  {providerLabel[provider]}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={form.reasoning}
                  onChange={(e) => updateForm('reasoning', e.target.checked)}
                />
              }
              label="Reasoning-Modell"
            />
            {form.provider === 'bedrock-converse' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={form.inferenceProfile}
                    onChange={(e) => updateForm('inferenceProfile', e.target.checked)}
                  />
                }
                label="Cross-Region-Inference-Profil (Geo-Präfix)"
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={form.enabled}
                  onChange={(e) => updateForm('enabled', e.target.checked)}
                />
              }
              label="Modell aktiv"
            />
            <TextField
              label="Max. Tokens"
              size="small"
              value={form.maxTokens}
              onChange={(e) => updateForm('maxTokens', e.target.value)}
              slotProps={{ htmlInput: { inputMode: 'numeric' } }}
              helperText="Maximale Antwortlänge. Leer lassen für den Server-Standard (MAX_TOKENS_DEFAULT)."
            />
            <TextField
              label="Temperature"
              size="small"
              value={form.temperature}
              onChange={(e) => updateForm('temperature', e.target.value)}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              helperText={`0–${temperatureMax(form.provider)}. Leer lassen für den Provider-Standard.${
                form.provider === 'bedrock' && form.reasoning
                  ? ' Bei Reasoning-Modellen (Anthropic) wird der Wert ignoriert.'
                  : ''
              }`}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Box>
            {dialogMode === 'edit' && (
              <Button
                color="error"
                onClick={() => {
                  const target = models.find((model) => model.id === form.modelId)
                  setDialogMode(undefined)
                  if (target) setDeleteTarget(target)
                }}
              >
                Löschen
              </Button>
            )}
          </Box>
          <Box display="flex" gap={1}>
            <Button onClick={() => setDialogMode(undefined)}>Abbrechen</Button>
            <Button variant="contained" onClick={() => void handleSave()}>
              Speichern
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(undefined)}>
        <DialogTitle>Modell löschen?</DialogTitle>
        <DialogContent>
          „{deleteTarget?.label}“ ({deleteTarget?.id}) wird aus dem Katalog entfernt. Chats, die
          dieses Modell verwenden, können erst nach Modellwechsel weiterschreiben.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(undefined)}>Abbrechen</Button>
          <Button color="error" onClick={() => void handleDelete()}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
